import { useEffect, useRef, useCallback, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuthContext } from '../context-state/AuthContext';
import { useMarketplaceContext } from '../context-state/MarketplaceContext';
import { inactivityManager } from '../utils/inactivityManager';
import {
  INACTIVITY_TIMEOUT_MINUTES,
  INACTIVITY_WARNING_MINUTES,
  ACTIVITY_DEBOUNCE_MS,
  INACTIVITY_EXCLUDED_ROUTES,
} from '../config-global';

/**
 * Pure helper function to convert minutes to milliseconds
 * @param {number} minutes - Time duration in minutes
 * @returns {number} Time duration in milliseconds
 * @example
 * minutesToMs(30) // returns 1800000 (30 minutes)
 * minutesToMs(2)  // returns 120000 (2 minutes)
 */
const minutesToMs = (minutes) => minutes * 60 * 1000;

const sanitizeMinutes = (value, fallback) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
};

/**
 * Custom hook for handling user inactivity auto-logout
 *
 * Features:
 * - Tracks user activity (mouse, keyboard, scroll, touch, route changes)
 * - Shows warning dialog before logout
 * - Syncs activity across browser tabs
 * - Excludes specific routes (scanner pages)
 * - Controlled by marketplace feature flag
 *
 * @returns {Object} { showWarning, resetTimer }
 */
export const useInactivityLogout = () => {
  const { logout, isAuthenticated } = useAuthContext();
  const { marketplace } = useMarketplaceContext();

  const location = useLocation();
  const navigate = useNavigate();

  // State
  const [showWarning, setShowWarning] = useState(false);

  // Refs for timers (don't cause re-renders)
  const logoutTimerRef = useRef(null);
  const warningTimerRef = useRef(null);
  const activityDebounceRef = useRef(null);
  const lastActivityRef = useRef(Date.now());


  // Feature is enabled via marketplace flag; defaults to false when config is missing
  const inactivityConfig = marketplace?.inactivityConfig;
  const isFeatureEnabled = inactivityConfig?.enabled ?? false


  const timeoutMinutes = sanitizeMinutes(inactivityConfig?.timeoutMinutes, INACTIVITY_TIMEOUT_MINUTES);
  const warningMinutes = sanitizeMinutes(inactivityConfig?.warningMinutes, INACTIVITY_WARNING_MINUTES);

  // Convert minutes to milliseconds using pure helper function
  const timeoutMs = minutesToMs(timeoutMinutes);
  const warningMs = minutesToMs(warningMinutes);

  /**
   * Check if current route should be excluded from inactivity tracking
   */
  const isExcludedRoute = INACTIVITY_EXCLUDED_ROUTES.some(route =>
    location.pathname.startsWith(route)
  );

  /**
   * Clear all timers
   */
  const clearTimers = useCallback(() => {
    if (logoutTimerRef.current) {
      clearTimeout(logoutTimerRef.current);
      logoutTimerRef.current = null;
    }
    if (warningTimerRef.current) {
      clearTimeout(warningTimerRef.current);
      warningTimerRef.current = null;
    }
    if (activityDebounceRef.current) {
      clearTimeout(activityDebounceRef.current);
      activityDebounceRef.current = null;
    }
  }, []);

  /**
   * Perform logout
   * Saves current path for redirect after re-login
   */
  const performLogout = useCallback((fromCrossTab = false) => {
    console.debug('🚪 Auto-logout due to inactivity');

    // Cleanup BEFORE navigate to avoid state updates after unmount
    clearTimers();
    setShowWarning(false);

    // Save current path for redirect after re-login (with error handling)
    try {
      sessionStorage.setItem('returnTo', location.pathname);
    } catch (error) {
      console.warn('Could not save return path:', error);
      // Continue with logout anyway - don't let storage failure block logout!
    }

    // Broadcast logout to other tabs (but NOT if this logout came from cross-tab sync)
    if (!fromCrossTab) {
      inactivityManager.broadcastLogout();
    }

    // Perform logout and navigate
    try {
      logout();
      navigate('/auth/login?reason=inactivity', { replace: true });
    } catch (error) {
      console.error('Logout or navigation failed:', error);
      // Fallback: force hard redirect to ensure user reaches login page
      window.location.href = '/auth/login?reason=inactivity';
    }
  }, [logout, navigate, location.pathname, clearTimers]);

  /**
   * Show warning dialog
   */
  const showWarningDialog = useCallback(() => {
    console.debug('⚠️ Warning dialog shown - auto-logout in', warningMs / 1000, 'seconds');
    setShowWarning(true);

    // Set timeout to auto-logout when warning period expires
    logoutTimerRef.current = setTimeout(() => {
      performLogout();
    }, warningMs);
  }, [performLogout, warningMs]);

  /**
   * Reset inactivity timer
   * Called on user activity or "Stay Logged In" button click
   */
  const resetTimer = useCallback((source = 'unknown') => {
    console.debug('⏱️ Timer reset from:', source);

    // Update last activity timestamp
    lastActivityRef.current = Date.now();

    // Clear existing timers
    clearTimers();

    // Hide warning if shown
    setShowWarning(false);

    // Broadcast activity to other tabs (but NOT if this reset came from cross-tab sync)
    if (source !== 'cross-tab-activity') {
      inactivityManager.broadcastActivity();
    }

    // Calculate when to show warning
    const timeUntilWarning = timeoutMs - warningMs;

    // Validate configuration
    if (timeUntilWarning <= 0) {
      console.error(
        'Invalid inactivity configuration: warning minutes must be less than timeout minutes',
        {
          timeoutMinutes,
          warningMinutes,
          defaultTimeoutMinutes: INACTIVITY_TIMEOUT_MINUTES,
          defaultWarningMinutes: INACTIVITY_WARNING_MINUTES
        }
      );
      return; // Don't set up timers with invalid config
    }

    // Set warning timer
    warningTimerRef.current = setTimeout(() => {
      showWarningDialog();
    }, timeUntilWarning);

  }, [clearTimers, showWarningDialog, timeoutMs, warningMs]);

  /**
   * Debounced activity handler
   * Prevents excessive timer resets from frequent events
   */
  const handleActivity = useCallback(() => {
    if (activityDebounceRef.current) {
      clearTimeout(activityDebounceRef.current);
    }

    activityDebounceRef.current = setTimeout(() => {
      resetTimer('user-activity');
    }, ACTIVITY_DEBOUNCE_MS);
  }, [resetTimer]);

  /**
   * Re-validate elapsed wall-clock time when the tab becomes visible again.
   *
   * A scheduled setTimeout can silently stop running while a tab is
   * backgrounded for a long time (browser tab freezing/throttling, or the
   * underlying OS session being suspended - e.g. a locked RDP session). In
   * that case the timer never fires, so we can't rely on it alone: on
   * visibility restore, compare real elapsed time against the last known
   * activity and log out immediately if the timeout has already passed.
   */
  useEffect(() => {
    if (!isFeatureEnabled || !isAuthenticated || isExcludedRoute) {
      return;
    }

    const handleVisibilityChange = () => {
      if (document.visibilityState !== 'visible') {
        return;
      }

      // Cross-tab activity (BroadcastChannel/storage) delivery isn't ordered
      // relative to visibilitychange per spec, so a queued "another tab was
      // active" message can't be relied on to have been processed yet.
      // Read the synchronously-persisted timestamp directly instead.
      const storedActivity = inactivityManager.getLastKnownActivity();
      const latestActivity = storedActivity && storedActivity > lastActivityRef.current
        ? storedActivity
        : lastActivityRef.current;
      lastActivityRef.current = latestActivity;

      const elapsed = Date.now() - latestActivity;

      if (elapsed >= timeoutMs) {
        performLogout();
        return;
      }

      if (elapsed >= timeoutMs - warningMs) {
        // The warning period was already due while the tab was hidden.
        // Show it now and schedule logout for the time actually remaining,
        // instead of restarting a full fresh warning window.
        clearTimers();
        setShowWarning(true);
        logoutTimerRef.current = setTimeout(() => {
          performLogout();
        }, timeoutMs - elapsed);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isFeatureEnabled, isAuthenticated, isExcludedRoute, timeoutMs, warningMs, performLogout, clearTimers]);

  /**
   * Handle cross-tab messages
   */
  useEffect(() => {
    if (!isFeatureEnabled || !isAuthenticated || isExcludedRoute) {
      return;
    }

    const unsubscribe = inactivityManager.subscribe((data) => {
      if (data.type === 'activity') {
        // Activity in another tab - reset our timer
        resetTimer('cross-tab-activity');
      } else if (data.type === 'logout') {
        // Logout in another tab - logout this tab too (don't re-broadcast)
        performLogout(true);
      }
    });

    return unsubscribe;
  }, [isFeatureEnabled, isAuthenticated, isExcludedRoute, resetTimer, performLogout]);

  /**
   * Setup activity listeners and handle route changes
   */
  useEffect(() => {
    // Don't track if feature disabled, user not authenticated, or on excluded route
    if (!isFeatureEnabled || !isAuthenticated || isExcludedRoute) {
      clearTimers();
      setShowWarning(false);
      return;
    }

    // Activity events to track
    const events = [
      'mousedown',
      'mousemove',
      'keydown',
      'scroll',
      'touchstart',
      'click',
    ];

    // Add event listeners
    events.forEach((event) => {
      window.addEventListener(event, handleActivity, { passive: true });
    });

    // Initialize/reset timer (on mount and route changes)
    resetTimer('mount-or-route-change');

    // Cleanup on unmount
    return () => {
      events.forEach((event) => {
        window.removeEventListener(event, handleActivity);
      });
      clearTimers();
    };
  }, [isFeatureEnabled, isAuthenticated, isExcludedRoute, handleActivity, resetTimer, clearTimers]);

  return {
    showWarning,
    resetTimer, // Exposed for "Stay Logged In" button
  };
};
